import type { ListProps, QueryStoreProps } from "./List.js";
import type { AsyncValueCallback, DataKey, Database, Item } from "shelving";
import { call } from "shelving";
import { ASIDE } from "../base/Aside.js";
import { closeEventDialog } from "../base/Dialogs.js";
import { wrapText } from "../util/react.js";
import { PaginateButton } from "./PaginateButton.js";

export interface PickerProps<T> extends ListProps {
	/** Is this the currently picked value? */
	isPicked?: ((v: T) => boolean) | undefined;
	/** Callback that is called when an item is picked. */
	onPick: AsyncValueCallback<T>;
}

/** Output a paginated set of results as a picker. */
export function Picker<T extends Database, K extends DataKey<T>>({
	mods = "flex-column gap-hairline",
	before = null,
	after = null,
	store,
	empty = `No ${store.collection}s`,
	more,
	detect,
	mapper,
	onPick,
	isPicked,
}: PickerProps<Item<T[K]>> & QueryStoreProps<T, K>): React.ReactElement | null {
	const { value, hasMore } = store;
	return value.length ? (
		<section className={mods}>
			{before}
			{value.map((item, i) => (
				<button
					type="button"
					key={i}
					className={`input ${isPicked && isPicked(item) ? "input-active" : ""}`}
					onClick={e => {
						call(onPick, item);
						closeEventDialog(e);
					}}
				>
					{mapper(item, i)}
				</button>
			))}
			{after}
			{more !== null && value.length && hasMore ? (
				<PaginateButton store={store} detect={detect} mods="size-small button-quiet">
					{more}
				</PaginateButton>
			) : null}
		</section>
	) : (
		wrapText(empty, ASIDE)
	);
}
